import React, { useState, useEffect, useRef } from "react";
import { Inbox, MousePointerClick, FileText, ShieldCheck, CalendarRange, Check } from "lucide-react";

function useScrollProgress(ref) {
  const [p, setP] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const el = ref.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const anchor = window.innerHeight * 0.72;
      setP(Math.min(1, Math.max(0, (anchor - r.top) / r.height)));
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [ref]);
  return p;
}

function RequestCard({ active }) {
  return (
    <div className={`rounded-2xl bg-white border border-slate-200 shadow-md p-5 transition-all duration-700 ${active ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="h-9 w-9 rounded-full bg-violet-100 flex items-center justify-center text-[11px] font-bold text-violet-700">MP</span>
          <div>
            <div className="text-sm font-semibold text-slate-900">Marc Petit</div>
            <div className="text-[11px] text-slate-500">il y a 2 min</div>
          </div>
        </div>
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-violet-600 text-white text-[10px] font-bold">
          <span className="h-1.5 w-1.5 rounded-full bg-white animate-ping" style={{ animationDuration: "1.4s" }} />
          Nouvelle demande
        </span>
      </div>
      <div className="mt-4 grid grid-cols-3 gap-2 text-center">
        {[["Véhicule", "Peugeot 208"], ["Dates", "12 – 15 juin"], ["Montant", "180 €"]].map(([l, v]) => (
          <div key={l} className="rounded-lg bg-slate-50 border border-slate-100 py-2">
            <div className="text-[10px] text-slate-500">{l}</div>
            <div className="text-[12px] font-bold text-slate-900">{v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ValidateCard({ active }) {
  const [confirmed, setConfirmed] = useState(false);
  useEffect(() => {
    if (!active) { setConfirmed(false); return; }
    const t = setTimeout(() => setConfirmed(true), 900);
    return () => clearTimeout(t);
  }, [active]);
  return (
    <div className={`rounded-2xl bg-white border border-slate-200 shadow-md p-5 transition-all duration-700 ${active ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-slate-900">Réservation #2841</div>
        <span
          className={`px-2.5 py-1 rounded-full text-[10px] font-bold transition-all duration-500 ${
            confirmed ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
          }`}
          data-testid="resa-status-chip"
        >
          {confirmed ? "✓ Confirmée" : "En attente"}
        </span>
      </div>
      <div className="mt-4 flex gap-2.5">
        <span
          className={`flex-1 text-center py-2.5 rounded-xl text-[13px] font-semibold transition-colors duration-500 ${
            confirmed ? "bg-emerald-600 text-white" : "bg-violet-600 text-white"
          }`}
          style={active && !confirmed ? { animation: "jnPress 0.9s ease-in-out 0.4s" } : undefined}
        >
          {confirmed ? "Acceptée ✓" : "Accepter"}
        </span>
        <span className="flex-1 text-center py-2.5 rounded-xl text-[13px] font-semibold border border-slate-200 text-slate-500">Refuser</span>
      </div>
      <p className="mt-3 text-[11px] text-slate-500 text-center">Un clic. Le reste est automatique.</p>
    </div>
  );
}

function DocsCard({ active }) {
  return (
    <div className="space-y-2.5">
      <div
        className="rounded-2xl bg-white border border-slate-200 shadow-md p-4 flex items-center gap-3"
        style={active ? { animation: "jnMaterialize 0.6s cubic-bezier(0.22,1,0.36,1) both", animationDelay: "150ms" } : { opacity: 0 }}
      >
        <span className="h-9 w-9 rounded-xl bg-violet-50 border border-violet-100 flex items-center justify-center"><FileText className="h-4 w-4 text-violet-600" /></span>
        <div className="flex-1">
          <div className="text-[13px] font-semibold text-slate-900">Contrat_M.Petit.pdf</div>
          <div className="text-[11px] text-slate-500">Généré automatiquement · prêt à signer</div>
        </div>
        <Check className="h-4 w-4 text-emerald-500" strokeWidth={3} />
      </div>
      <div
        className="rounded-2xl bg-white border border-slate-200 shadow-md p-4 flex items-center gap-3"
        style={active ? { animation: "jnMaterialize 0.6s cubic-bezier(0.22,1,0.36,1) both", animationDelay: "420ms" } : { opacity: 0 }}
      >
        <span className="h-9 w-9 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center"><ShieldCheck className="h-4 w-4 text-emerald-600" /></span>
        <div className="flex-1">
          <div className="text-[13px] font-semibold text-slate-900">Caution 800 €</div>
          <div className="text-[11px] text-slate-500">Empreinte bancaire · rien n&apos;est débité</div>
        </div>
        <Check className="h-4 w-4 text-emerald-500" strokeWidth={3} />
      </div>
    </div>
  );
}

function PlanningCard({ active }) {
  return (
    <div className={`rounded-2xl bg-white border border-slate-200 shadow-md p-5 transition-all duration-700 ${active ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
      <div className="text-[11px] font-bold text-slate-500 mb-3">Planning · Peugeot 208</div>
      <div className="grid grid-cols-7 gap-1 text-center mb-2">
        {["10", "11", "12", "13", "14", "15", "16"].map((d) => (
          <div key={d} className="text-[10px] text-slate-400 font-semibold">{d}</div>
        ))}
      </div>
      <div className="relative h-9 rounded-lg bg-slate-50 border border-slate-100 overflow-hidden">
        <div
          className="absolute top-1 bottom-1 left-[29%] rounded-md bg-gradient-to-r from-violet-600 to-violet-500 flex items-center px-2 overflow-hidden transition-all duration-1000 ease-out"
          style={{ width: active ? "56%" : "0%" }}
        >
          <span className="text-[10px] font-bold text-white whitespace-nowrap">208 · M. Petit</span>
          {active && (
            <span
              aria-hidden
              className="absolute inset-y-0 w-16 bg-gradient-to-r from-transparent via-white/40 to-transparent"
              style={{ animation: "jnSweep 1.6s ease-out 0.8s both" }}
            />
          )}
        </div>
      </div>
      <p className="mt-3 text-[11px] text-slate-500">Véhicule bloqué · aucun risque de double réservation</p>
    </div>
  );
}

const STEPS = [
  {
    icon: Inbox,
    title: "La demande arrive",
    desc: "Marc veut la 208 du 12 au 15 juin. Vous recevez tout : profil, dates, montant. Aucune ressaisie.",
    Card: RequestCard,
  },
  {
    icon: MousePointerClick,
    title: "Vous validez en 1 clic",
    desc: "Un seul bouton. Le statut bascule, le client est notifié instantanément.",
    Card: ValidateCard,
  },
  {
    icon: FileText,
    title: "Contrat & caution, déjà prêts",
    desc: "Le contrat est généré, la caution pré-autorisée par empreinte bancaire. Vous n'avez rien fait.",
    Card: DocsCard,
  },
  {
    icon: CalendarRange,
    title: "Le planning se met à jour",
    desc: "Le véhicule est bloqué sur le calendrier. Toute l'équipe le voit en temps réel.",
    Card: PlanningCard,
  },
];

export default function ReservationShowcase() {
  const ref = useRef(null);
  const progress = useScrollProgress(ref);

  return (
    <div data-testid="reservation-showcase">
      <div className="max-w-2xl mb-14">
        <div className="text-xs font-bold tracking-[0.2em] uppercase text-violet-600 mb-4">Gestion des réservations</div>
        <h3 className="font-heading text-2xl md:text-3xl lg:text-4xl font-medium tracking-tight leading-tight text-slate-900">
          De la demande au planning.
          <br />
          <span className="text-violet-600">30 secondes, zéro ressaisie.</span>
        </h3>
      </div>

      <div ref={ref} className="relative">
        {/* Ligne de vie */}
        <div aria-hidden className="absolute left-5 md:left-1/2 md:-translate-x-1/2 top-0 bottom-0 w-[2px] bg-slate-200 rounded-full" />
        <div
          aria-hidden
          className="absolute left-5 md:left-1/2 md:-translate-x-1/2 top-0 w-[2px] bg-gradient-to-b from-violet-600 to-fuchsia-500 rounded-full transition-[height] duration-300 ease-out"
          style={{ height: `${progress * 100}%` }}
        />

        <div className="space-y-16 md:space-y-24">
          {STEPS.map((step, i) => {
            const active = progress >= (i + 0.35) / STEPS.length;
            const Icon = step.icon;
            const Card = step.Card;
            const cardLeft = i % 2 === 1;
            return (
              <div key={step.title} className="relative grid md:grid-cols-2 gap-6 md:gap-16 items-center pl-14 md:pl-0" data-testid={`resa-step-${i + 1}`}>
                {/* Nœud sur la ligne */}
                <div
                  className={`absolute left-5 md:left-1/2 -translate-x-1/2 h-10 w-10 rounded-full border-2 flex items-center justify-center transition-all duration-500 z-10 ${
                    active ? "bg-violet-600 border-violet-600 text-white shadow-lg shadow-violet-600/30 scale-100" : "bg-white border-slate-200 text-slate-400 scale-90"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                </div>
                {/* Texte */}
                <div className={`${cardLeft ? "md:order-2 md:pl-16" : "md:pr-16 md:text-right"}`}>
                  <div className={`text-[11px] font-bold tracking-[0.14em] uppercase transition-colors duration-500 ${active ? "text-violet-600" : "text-slate-400"}`}>
                    Étape {i + 1}
                  </div>
                  <h4 className={`mt-2 font-heading text-xl md:text-2xl font-semibold tracking-tight transition-colors duration-500 ${active ? "text-slate-900" : "text-slate-400"}`}>
                    {step.title}
                  </h4>
                  <p className={`mt-2 text-sm leading-relaxed transition-colors duration-500 ${active ? "text-slate-600" : "text-slate-400"}`}>{step.desc}</p>
                </div>
                {/* Visuel */}
                <div className={cardLeft ? "md:order-1 md:pr-16" : "md:pl-16"}>
                  <Card active={active} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
